import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  getCurrentUser,
  parseSocialCallback,
  exchangeAuthCode,
  establishTokenSession,
  getLinkRequired,
  getOAuthUrl,
  SocialProvider,
} from '../services/cognito';
import { crmService } from '../services/api';
import { buildUserFromToken } from '../utils/buildUserFromToken';
import { trackLogin, trackIdentify } from '../services/analytics';

interface AuthCallbackProps {
  setAuthenticated: (auth: boolean) => void;
  setUser: (user: any) => void;
}

interface CallbackErrorState {
  message: string;
  cancelled: boolean;
}

interface LinkRequiredState {
  email?: string;
}

const AuthCallback: React.FC<AuthCallbackProps> = ({ setAuthenticated, setUser }) => {
  const navigate = useNavigate();
  const [callbackError, setCallbackError] = useState<CallbackErrorState | null>(null);
  const [linkRequired, setLinkRequired] = useState<LinkRequiredState | null>(null);
  // Single-flight guard: a double effect invoke (StrictMode, remount) would
  // consume the one-time code twice — the second exchange returns 400
  // invalid_grant and the user sees the error screen on a flow that already
  // succeeded. (Live flake documented in E-58/E-59 hardening notes.)
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    if (exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;

    // E-58 social redirect detection MUST run before the email/password path:
    // the Hosted-UI callback carries ?code= (auth-code exchange), #id_token=
    // (implicit), or ?error= (provider error / user cancelled). A plain
    // callback (no params) is the Cognito sign-in flow below.
    const outcome = parseSocialCallback(window.location.href);

    // Scrub the one-time credentials from the address bar IMMEDIATELY after
    // parsing. Leaving ?code= in the URL meant every reload (including the
    // "Try again" button below) re-posted the spent code, and Cognito
    // answered 400 invalid_grant forever — the persistent Google 400.
    if (outcome.type === 'code' || outcome.type === 'tokens' || outcome.type === 'error') {
      window.history.replaceState(null, document.title, window.location.pathname);
    }

    const completeWithSession = (cognitoUser: any, session: any, opts?: { social?: boolean }) => {
      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      const decoded: any = jwtDecode(idToken);

      // F-378: never auto-land a user whose social email matches an existing
      // password account — surface the readable linking prompt instead of a
      // silent takeover or a blank screen.
      const link = getLinkRequired(decoded);
      if (link.required) {
        setLinkRequired({ email: link.email });
        return;
      }

      localStorage.setItem('idToken', idToken);
      localStorage.setItem('accessToken', accessToken);

      if (opts?.social) trackLogin('social');
      trackIdentify({
        distinctId: decoded.sub || decoded['cognito:username'] || idToken,
        email: decoded.email,
        name: decoded.name || decoded.given_name,
        plan: decoded['custom:subscriptionTier'] || decoded['custom:subscriptionStatus'] || null,
        signupDate: typeof decoded.auth_time === 'number'
          ? new Date(decoded.auth_time * 1000).toISOString()
          : undefined,
      });

      // Invited users (custom:tenantId) land inside their invited tenant.
      if (decoded['custom:tenantId']) {
        try {
          localStorage.setItem('tenantId', decoded['custom:tenantId']);
        } catch {
          /* storage error — tenant is server-authoritative */
        }
      }

      // Populate App-level user state before navigating so the Dashboard
      // route renders with a fully-hydrated user object — previously
      // user stayed null until the next mount, forcing a manual refresh.
      setUser(buildUserFromToken(cognitoUser, idToken));
      setAuthenticated(true);

      // Check Units and Subscription
      const units = parseInt(decoded['custom:units'] || '0', 10);
      const isSubscriptionActive = decoded['custom:subscription_active'] === 'true' || decoded['subscription_active'] === true; // Adjust key based on your Lambda

      if (units > 1 && !isSubscriptionActive) {
        // Redirect to renewal/payment if units > 1 and no active subscription
        // You might want to pass the units count to pre-select the plan
        console.log('Units > 1 and no active subscription. Redirecting to renewal.');
        navigate('/renewal?reason=subscription_required');
        return;
      }

      // Check for pending Brevo sync
      const pendingSync = sessionStorage.getItem('pending_brevo_sync');
      if (pendingSync) {
        try {
          const contactData = JSON.parse(pendingSync);
          console.log('Processing deferred Brevo sync...');

          // Trigger sync but don't block navigation (unless critical)
          // We'll let it run in background
          crmService.createContact(contactData).then(() => {
            console.log('Deferred Brevo sync successful');
            sessionStorage.removeItem('pending_brevo_sync');
          }).catch((err: any) => {
            console.error('Deferred Brevo sync failed', err);
          });
        } catch (e) { console.error('Error parsing pending sync', e); }
      }

      // Domain-based Routing
      const hostname = window.location.hostname;
      const isFSP = hostname.includes('fsp') || hostname.includes('food-safety') || hostname.includes('foodsafety');
      const isGDPR = hostname.includes('gdpr');

      if (isFSP) {
        navigate('/food-safety/dashboard');
      } else if (isGDPR) {
        navigate('/gdpr');
      } else {
        // Default (Landlord Pro)
        navigate('/dashboard');
      }
    };
    const runExistingFlow = () => {
      const user = getCurrentUser();
      if (user) {
        user.getSession((err: any, session: any) => {
          if (err) {
            navigate('/login');
            return;
          }
          completeWithSession(user, session, { social: outcome.type !== 'none' });
        });
      } else {
        navigate('/login');
      }
    };

    const run = async () => {
      if (outcome.type === 'error') {
        setCallbackError({
          message: outcome.errorDescription || outcome.error || 'Sign-in could not be completed.',
          cancelled: outcome.cancelled,
        });
        return;
      }
      if (outcome.type === 'code' || outcome.type === 'tokens') {
        try {
          const tokens = outcome.type === 'code'
            ? await exchangeAuthCode(outcome.code)
            : {
                id_token: outcome.idToken,
                access_token: outcome.accessToken,
                refresh_token: outcome.refreshToken,
              };
          const decoded: any = jwtDecode(tokens.id_token);
          establishTokenSession(
            {
              idToken: tokens.id_token,
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token,
            },
            decoded
          );
          sessionStorage.removeItem('pending_social_provider');
        } catch (err: any) {
          setCallbackError({
            message: err?.message || 'Sign-in could not be completed. Please try again.',
            cancelled: false,
          });
          return;
        }
      }
      runExistingFlow();
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (callbackError) {
    // Retry must restart the AUTHORIZE redirect with a fresh code — the old
    // window.location.reload() re-posted the spent ?code= and Cognito kept
    // answering 400 invalid_grant (the persistent Google 400). The provider
    // is stashed by SocialLoginButtons before it redirects.
    const retrySocial = () => {
      const provider = sessionStorage.getItem('pending_social_provider');
      if (provider === 'Google' || provider === 'Microsoft' || provider === 'Apple') {
        sessionStorage.removeItem('pending_social_provider');
        try {
          window.location.href = getOAuthUrl(provider as SocialProvider);
          return;
        } catch {
          /* fall through to login */
        }
      }
      navigate('/login');
    };

    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: '16px' }}>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 max-w-md w-full text-center" data-testid={callbackError.cancelled ? 'callback-cancelled' : 'callback-error'}>
          <div className="text-4xl mb-3">{callbackError.cancelled ? '↩' : '⚠'}</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {callbackError.cancelled ? 'You cancelled the sign-in' : 'We could not sign you in'}
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            {callbackError.cancelled
              ? 'No changes were made to your account. You can try again, or use your email and password.'
              : callbackError.message}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-4 py-2 rounded font-semibold hover:bg-blue-700"
              data-testid="back-to-login"
            >
              Back to Login
            </button>
            <button
              onClick={retrySocial}
              className="bg-gray-100 text-gray-800 px-4 py-2 rounded font-semibold hover:bg-gray-200"
              data-testid="retry-callback"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (linkRequired) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: '16px' }}>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 max-w-md w-full text-center" data-testid="link-required-prompt">
          <div className="text-4xl mb-3">🔗</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">This email already has an account</h1>
          <p className="text-sm text-gray-600 mb-6">
            {linkRequired.email ? (
              <>
                <strong>{linkRequired.email}</strong> already has a password-protected account.
                Sign in with that account&apos;s password to link your social login safely — we never
                take over an existing account without your confirmation.
              </>
            ) : (
              'This email already has a password-protected account. Use its password to sign in and link your social login safely.'
            )}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-4 py-2 rounded font-semibold hover:bg-blue-700"
              data-testid="link-use-password"
            >
              Use existing password
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="bg-gray-100 text-gray-800 px-4 py-2 rounded font-semibold hover:bg-gray-200"
              data-testid="link-create-new"
            >
              Create a separate account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <div>Loading...</div>;
};

export default AuthCallback;